import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { fetchUserBalance, transferBalance } from 'store/authSlice';
import { authService } from 'api/auth';
import { Alert } from 'components/alert/alert';
import CodeValidation from 'components/codeValidation/codeValidation';
import styles from './tabContent.module.scss';
import { ActionButton } from 'components/actionButton';
import { SwapIcon } from 'components/icons';
import type {
  RequestVaultTransferResponse,
  VaultLockDuration,
  VaultTransferChallenge,
} from 'types/auth';
import { formatLocalTime } from 'utils/formatDate';
import { generateIdempotencyKey } from 'utils/idempotency';

interface DepositTabProps {
  onSwap?: () => void;
}

function extractError(err: any, fallback: string): string {
  const msg = err?.response?.data?.message ?? err?.data?.message ?? err?.message;
  if (Array.isArray(msg)) return msg[0] ?? fallback;
  return typeof msg === 'string' && msg ? msg : fallback;
}

/**
 * Still-locked entries first. The API sorts by unlock date, so unlocked rows —
 * every No Lock deposit among them — would otherwise take the two visible
 * slots and push real locks behind "Show All". The sort is stable, so each
 * group keeps the API's order.
 */
function lockedFirst(locks: any[]): any[] {
  return [...locks].sort((a, b) => Number(!!a.isUnlocked) - Number(!!b.isUnlocked));
}

const DepositTab: React.FC<DepositTabProps> = ({ onSwap }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const lockDurations = useMemo<{ value: VaultLockDuration; label: string }[]>(
    () => [
      { value: '1d', label: t('vault.lockDuration.oneDay', '1 Day') },
      { value: '1w', label: t('vault.lockDuration.oneWeek', '1 Week') },
      { value: '1m', label: t('vault.lockDuration.oneMonth', '1 Month') },
      { value: 'none', label: t('vault.lockDuration.noLock', 'No Lock') },
    ],
    [t],
  );
  const currentBalance = user?.balances?.[0];
  const withdrawableBalance = currentBalance?.withdrawable || '0.00';
  const vaultBalance = currentBalance?.vault || '0.00';
  const currencyCode = currentBalance?.currency || 'USD';
  const [vaultLocks, setVaultLocks] = useState<any[]>([]);
  const [amount, setAmount] = useState('');
  const [lockDuration, setLockDuration] = useState<VaultLockDuration>('1d');
  const [alert, setAlert] = useState<{
    message: string;
    type: 'success' | 'error';
    variant: 'local' | 'toast';
  } | null>(null);
  const [loadingLocks, setLoadingLocks] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [showAllLocks, setShowAllLocks] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [challenge, setChallenge] = useState<VaultTransferChallenge>('email');
  const [maskedEmail, setMaskedEmail] = useState<string | undefined>();
  const [verifyError, setVerifyError] = useState('');

  const fetchLocks = useCallback(async () => {
    setLoadingLocks(true);
    try {
      const res = await authService.getVaultLocks(currencyCode);
      const locks = res?.data?.locks || res?.locks || [];
      setVaultLocks(Array.isArray(locks) ? lockedFirst(locks) : []);
    } catch (err) {
      console.error('Failed to fetch vault locks', err);
    } finally {
      setLoadingLocks(false);
    }
  }, [currencyCode]);

  useEffect(() => {
    fetchLocks();
  }, [fetchLocks]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\$/g, '');
    if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
      setAmount(rawValue);
      setAlert(null);
    }
  };

  /** The money has landed in the vault — shared by both deposit paths. */
  const finishDeposit = () => {
    setAlert({
      message: t('vault.messages.transferredToVault', 'Balance transferred to vault.'),
      type: 'success',
      variant: 'toast',
    });
    setAmount('');
    setLockDuration('1d');
    // The transfer responses carry fresh balances, but the wallet reads them
    // from the auth slice — refetch so every balance surface (including the
    // vault's Available amount) stays in sync.
    dispatch(fetchUserBalance());
    fetchLocks();
  };

  /**
   * No Lock traps nothing, so there is no approval step: the money moves
   * straight away through the plain transfer endpoint, just like a
   * vault → main withdrawal.
   */
  const depositWithoutLock = async () => {
    const resultAction = await dispatch(
      transferBalance({
        fromType: 'main',
        toType: 'vault',
        amount,
        currencyCode,
        lockDuration: 'none',
        idempotencyKey: generateIdempotencyKey(),
      }),
    );

    if (transferBalance.fulfilled.match(resultAction)) {
      finishDeposit();
    } else {
      setAlert({
        message:
          (resultAction.payload as string) || t('vault.messages.transferFailed', 'Transfer failed'),
        type: 'error',
        variant: 'toast',
      });
    }
  };

  /**
   * No Lock moves the money at once. A locking span only starts the transfer:
   * the backend checks the amount and issues an approval challenge (emailed
   * code, or the authenticator app when the player has 2FA on), and the code
   * screen opens on success.
   */
  const handleDeposit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setAlert({
        message: t('vault.messages.invalidAmount', 'Please enter a valid amount'),
        type: 'error',
        variant: 'local',
      });
      return;
    }

    if (parseFloat(amount) > parseFloat(withdrawableBalance)) {
      setAlert({
        message: t('vault.messages.exceedsWithdrawable', 'Amount exceeds withdrawable balance'),
        type: 'error',
        variant: 'local',
      });
      return;
    }

    setAlert(null);
    setIsTransferring(true);

    try {
      if (lockDuration === 'none') {
        await depositWithoutLock();
        return;
      }

      const res = await authService.requestVaultTransfer({
        amount,
        currencyCode,
        lockDuration,
      });
      const data: RequestVaultTransferResponse = res?.data || res;
      setChallenge(data?.challenge || 'email');
      setMaskedEmail(data?.maskedEmail);
      setIsVerifying(true);
    } catch (err: any) {
      setAlert({
        message: extractError(
          err,
          t('vault.messages.failedToStart', 'Failed to start the transfer'),
        ),
        type: 'error',
        variant: 'toast',
      });
    } finally {
      setIsTransferring(false);
    }
  };

  /** Step 2 of a locking deposit: the code approves it and the money moves. */
  const handleVerify = async (code: string) => {
    setVerifyError('');
    try {
      await authService.confirmVaultTransfer(code);
      setIsVerifying(false);
      finishDeposit();
    } catch (err: any) {
      setVerifyError(
        extractError(err, t('vault.messages.invalidOrExpiredCode', 'Invalid or expired code')),
      );
    }
  };

  if (isVerifying) {
    return (
      <div className={styles.tabWrapper}>
        <CodeValidation
          isOpen={isVerifying}
          inline={true}
          title={t('vault.verify.title', 'Verify Vault Transfer')}
          error={verifyError}
          subtitle={
            challenge === 'authenticator' ? (
              <>
                {t(
                  'vault.verify.authenticatorSubtitle',
                  'Enter the current code from your authenticator app to approve this transfer.',
                )}
              </>
            ) : (
              <>
                {t(
                  'vault.verify.emailSubtitle',
                  'Please enter the otp code we have sent to your mail:',
                )}{' '}
                <strong>{maskedEmail}</strong>
              </>
            )
          }
          onComplete={handleVerify}
          onChange={() => setVerifyError('')}
          onBack={() => {
            setIsVerifying(false);
            setVerifyError('');
          }}
        />
      </div>
    );
  }

  return (
    <div className={styles.tabWrapper}>
      <Alert
        message={alert?.message}
        type={alert?.type}
        variant={alert?.variant}
        className={styles.alert}
        onClose={() => setAlert(null)}
      />

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <span className={styles.cardTitle}>{t('vault.balance', 'Balance')}</span>
          <span className={styles.cardSubtitle}>
            {t('vault.withdrawable', 'Withdrawable: ${{amount}}', { amount: withdrawableBalance })}
          </span>
        </div>

        <div className={styles.inputRow}>
          <input
            type="text"
            className={styles.amountInput}
            placeholder="$0.00"
            value={amount ? `$${amount}` : ''}
            onChange={handleAmountChange}
          />
          <button
            className={styles.maxBtn}
            onClick={() => {
              setAmount(withdrawableBalance);
              setAlert(null);
            }}
          >
            {t('vault.max', 'Max')}
          </button>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <span className={styles.cardTitle}>{t('vault.lockDuration.title', 'Lock Duration')}</span>
          <span className={styles.cardSubtitle}>
            {lockDuration === 'none'
              ? t('vault.lockDuration.anytime', 'Funds can be withdrawn anytime')
              : t('vault.lockDuration.inaccessible', 'Funds will be inaccessible for this period')}
          </span>
        </div>
        <div className={styles.durationTabs} role="radiogroup" aria-label="Lock duration">
          {lockDurations.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={lockDuration === value}
              className={`${styles.durationTab} ${
                lockDuration === value ? styles.durationTabActive : ''
              }`}
              onClick={() => {
                setLockDuration(value);
                setAlert(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.swapWrapper}>
        <button className={styles.swapBtn} onClick={onSwap}>
          <SwapIcon width="16" height="16" />
        </button>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <span className={styles.cardTitle}>{t('vault.vault', 'Vault')}</span>
          <div className={styles.cardSubtitleColumn}>
            <div className={styles.cardSubtitleRow}>
              <span className={styles.cardSubtitle}>
                {t('vault.currentBalance', 'Current Balance: ${{amount}}', {
                  amount: vaultBalance,
                })}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.locksList}>
          {loadingLocks ? (
            <>
              <div className={styles.skeletonItem}>
                <div className={styles.skeletonHeader}>
                  <div className={`${styles.skeletonBox} ${styles.skeletonAmount}`}></div>
                  <div className={`${styles.skeletonBox} ${styles.skeletonStatus}`}></div>
                </div>
                <div className={`${styles.skeletonBox} ${styles.skeletonDate}`}></div>
              </div>
              <div className={styles.skeletonItem}>
                <div className={styles.skeletonHeader}>
                  <div className={`${styles.skeletonBox} ${styles.skeletonAmount}`}></div>
                  <div className={`${styles.skeletonBox} ${styles.skeletonStatus}`}></div>
                </div>
                <div className={`${styles.skeletonBox} ${styles.skeletonDate}`}></div>
              </div>
            </>
          ) : vaultLocks.length > 0 ? (
            <>
              {(showAllLocks ? vaultLocks : vaultLocks.slice(0, 2)).map(
                (lock: any, index: number) => (
                  <div key={lock.id || index} className={styles.lockItem}>
                    <div className={styles.lockItemHeader}>
                      <span className={styles.lockAmount}>
                        <span className={styles.amountLabel}>
                          {t('vault.locks.remaining', 'Remaining:')}
                        </span>{' '}
                        ${lock.remainingAmount}{' '}
                        <span className={styles.amountLabel}>
                          {t('vault.locks.amount', '/ Amount:')}
                        </span>{' '}
                        ${lock.amount}
                      </span>
                      <span
                        className={`${styles.lockStatus} ${lock.isUnlocked ? styles.unlocked : styles.locked}`}
                      >
                        {lock.isUnlocked
                          ? t('vault.locks.unlocked', 'Unlocked')
                          : t('vault.locks.locked', 'Locked')}
                      </span>
                    </div>
                    <span className={styles.lockDateText}>
                      {t('vault.locks.unlockDate', 'Unlock Date: {{date}}', {
                        date: formatLocalTime(lock.lockedUntil),
                      })}
                    </span>
                  </div>
                ),
              )}
              {vaultLocks.length > 2 && (
                <button
                  className={styles.showAllBtn}
                  onClick={() => setShowAllLocks(!showAllLocks)}
                >
                  {showAllLocks
                    ? t('vault.locks.showLess', 'Show Less')
                    : t('vault.locks.showAll', 'Show All ({{count}})', {
                        count: vaultLocks.length,
                      })}
                </button>
              )}
            </>
          ) : (
            <div className={styles.noLocks}>
              {t('vault.locks.noLocks', 'No active vault locks')}
            </div>
          )}
        </div>
      </div>

      <ActionButton onClick={handleDeposit} isLoading={isTransferring}>
        {t('vault.transferToVault', 'Transfer to Vault')}
      </ActionButton>
    </div>
  );
};

export default DepositTab;
